import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

export type UnderlineTabsColorScheme =
  | "default"
  | "success"
  | "warning"
  | "info"
  | "destructive";

/**
 * Kept for API compatibility. All three sizes now render at the canonical
 * 36px toolbar height — only the icon metrics differ. Prefer omitting it.
 */
export type UnderlineTabsSize = "sm" | "default" | "lg";

export interface UnderlineTabsProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Root
> {
  colorScheme?: UnderlineTabsColorScheme;
  size?: UnderlineTabsSize;
  className?: string;
}

export interface UnderlineTabsListProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> {
  bordered?: boolean;
  className?: string;
}

export interface UnderlineTabsTriggerProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> {
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export interface UnderlineTabsContentProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Content
> {
  className?: string;
}

interface UnderlineTabsContextValue {
  colorScheme: UnderlineTabsColorScheme;
  size: UnderlineTabsSize;
}

const UnderlineTabsContext = React.createContext<UnderlineTabsContextValue>({
  colorScheme: "default",
  size: "default",
});

const useUnderlineTabsContext = () => React.useContext(UnderlineTabsContext);

const underlineTabsTriggerVariants = cva(
  [
    "relative inline-flex h-toolbar shrink-0 items-center justify-center gap-1.5",
    "text-xs font-medium whitespace-nowrap cursor-pointer bg-transparent",
    "text-text-tertiary",
    "transition-[color] duration-100 ease-out-fast",
    "hover:text-text",
    "focus-visible:outline-none focus-bar",
    "disabled:pointer-events-none disabled:text-text-disabled",
    "data-[state=active]:text-text",
    // 2px active underline — the §1.1 selected-state marker. With the list's
    // bottom hairline gone it sits flush on the list's bottom edge rather than
    // straddling a 1px rule (`docs/borderless-contract.md` §7: absorb the 1px).
    "after:absolute after:inset-x-0 after:bottom-0 after:h-0.5",
    "after:opacity-0 data-[state=active]:after:opacity-100",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      colorScheme: {
        default: "after:bg-accent",
        success: "after:bg-success data-[state=active]:text-success",
        warning: "after:bg-warning data-[state=active]:text-warning",
        info: "after:bg-info data-[state=active]:text-info",
        destructive: "after:bg-danger data-[state=active]:text-danger",
      },
      size: {
        sm: "[&_svg:not([class*='size-'])]:size-3",
        default: "[&_svg:not([class*='size-'])]:size-3.5",
        lg: "[&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      colorScheme: "default",
      size: "default",
    },
  },
);

const underlineTabsListVariants = cva(
  [
    "flex h-toolbar shrink-0 items-center gap-4",
    "overflow-x-auto",
    "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
    "relative",
  ].join(" "),
  {
    variants: {
      // Retained for API compatibility only. Under the borderless contract a
      // tab list is separated by the 2px active marker and space, never by a
      // bottom rule (§1), so neither branch draws an edge.
      bordered: {
        true: "",
        false: "",
      },
    },
    defaultVariants: {
      bordered: true,
    },
  },
);

const UnderlineTabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  UnderlineTabsProps
>(({ className, colorScheme = "default", size = "default", ...props }, ref) => {
  const contextValue = React.useMemo(
    () => ({ colorScheme, size }),
    [colorScheme, size],
  );

  return (
    <UnderlineTabsContext.Provider value={contextValue}>
      <TabsPrimitive.Root
        ref={ref}
        data-slot="underline-tabs"
        className={cn("flex flex-col", className)}
        {...props}
      />
    </UnderlineTabsContext.Provider>
  );
});
UnderlineTabs.displayName = "UnderlineTabs";

const UnderlineTabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  UnderlineTabsListProps
>(({ className, bordered = true, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    data-slot="underline-tabs-list"
    className={cn(underlineTabsListVariants({ bordered }), className)}
    {...props}
  />
));
UnderlineTabsList.displayName = "UnderlineTabsList";

const UnderlineTabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  UnderlineTabsTriggerProps
>(({ className, icon, badge, children, disabled, ...props }, ref) => {
  const { colorScheme, size } = useUnderlineTabsContext();

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot="underline-tabs-trigger"
      disabled={disabled}
      className={cn(
        underlineTabsTriggerVariants({ colorScheme, size }),
        className,
      )}
      {...props}
    >
      {icon}
      <span>{children}</span>
      {badge !== undefined && badge !== null && (
        <span className="text-xs tabular-nums text-text-tertiary">
          {badge}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
});
UnderlineTabsTrigger.displayName = "UnderlineTabsTrigger";

const UnderlineTabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  UnderlineTabsContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    data-slot="underline-tabs-content"
    className={cn(
      "mt-3 min-h-0 flex-1 outline-none focus-visible:outline-none focus-bar",
      className,
    )}
    {...props}
  />
));
UnderlineTabsContent.displayName = "UnderlineTabsContent";

export {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
  underlineTabsTriggerVariants,
  underlineTabsListVariants,
};
